import { Injectable, signal, effect } from '@angular/core';
import { ethers } from 'ethers';
import { getNetworkConfigById, NETWORKS, WALLET_CONNECT_PROJECT_ID } from '../config/network.config';
import { ERC20_ABI, SWAP_ROUTER_ABI, POSITION_MANAGER_ABI, FARMING_ABI } from '../config/contracts.config';
import { environment } from '../../environments/environment';

import { createAppKit } from '@reown/appkit';
import { EthersAdapter } from '@reown/appkit-adapter-ethers';
import { mainnet, sepolia, bsc, polygon, bscTestnet, polygonAmoy } from '@reown/appkit/networks';

const metadata = {
  name: 'DEX',
  description: 'Decentralized Exchange',
  url: typeof window !== 'undefined' ? window.location.origin : 'https://defi-user.pages.dev',
  icons: ['https://avatars.githubusercontent.com/u/37784886'],
  redirect: {
    native: 'dex://',
    universal: typeof window !== 'undefined' ? window.location.origin : 'https://defi-user.pages.dev',
  },
};

const customSepolia = {
  ...sepolia,
  rpcUrls: {
    default: { http: ['https://ethereum-sepolia-rpc.publicnode.com', 'https://1rpc.io/sepolia'] },
    public: { http: ['https://ethereum-sepolia-rpc.publicnode.com', 'https://1rpc.io/sepolia'] },
  },
};

export const appkit = createAppKit({
  adapters: [new EthersAdapter()],
  networks: [mainnet, customSepolia, bsc, polygon, bscTestnet, polygonAmoy],
  defaultNetwork: environment.networkMode === 'TESTNET' ? customSepolia : mainnet,
  metadata,
  projectId: WALLET_CONNECT_PROJECT_ID,
  featuredWalletIds: [
    'c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96', // MetaMask
    '10164c015b67eef090d8ed136a68f0a049d56ff61a7a032906b3e839e9259f93', // Trust/Rabby
  ],
  features: {
    email: false,
    socials: false,
    analytics: false,
    allWallets: false,
  },
  enableInjected: true,
  enableCoinbase: true,
});

@Injectable({
  providedIn: 'root',
})
export class Web3Service {
  public address = signal<string>('');
  public balance = signal<number>(0);
  public connectedProvider = signal<string>('');
  public chainId = signal<number>(0);
  public isConnected = signal<boolean>(false);
  private ethersProvider: ethers.BrowserProvider | null = null;

  constructor() {
    this.setupAppKitListeners();
    this.checkInitialState();
  }



  private async checkInitialState() {
    try {
      const provider = appkit.getWalletProvider();
      if (provider && !this.isConnected()) {
        const browserProvider = new ethers.BrowserProvider(provider as any);
        const signer = await browserProvider.getSigner();
        const addr = await signer.getAddress();

        if (addr) {
          this.ethersProvider = browserProvider;
          this.isConnected.set(true);
          this.address.set(addr);
          await this.syncAccountState();
        }
      }
    } catch (e) {
      // Silently ignore if not connected
    }
  }

  private setupAppKitListeners() {
    appkit.subscribeAccount(async (state) => {
      if (state.isConnected && state.address) {
        this.isConnected.set(true);
        this.address.set(state.address);
        const provider = appkit.getWalletProvider();
        if (provider) {
          this.ethersProvider = new ethers.BrowserProvider(provider as any);
          await this.syncAccountState();
        }
      } else {
        // Disconnected
        this.isConnected.set(false);
        this.address.set('');
        this.balance.set(0);
        this.ethersProvider = null;
      }
    });

    // Listen for network changes
    appkit.subscribeNetwork(async (state) => {
      console.log('[DEBUG] AppKit subscribeNetwork fired. state:', state, 'isConnected:', this.isConnected());
      // ONLY allow AppKit to dictate the network if the wallet is actually connected!
      // Otherwise it overrides our UI's selected network with its internal default.
      if (state.chainId && this.isConnected()) {
        console.log('[DEBUG] Web3Service overriding chainId to:', Number(state.chainId));
        this.chainId.set(Number(state.chainId));
        await this.syncAccountState();
      }
    });
  }

  async autoConnect(): Promise<void> {
    // No-op: Handled by AppKit
  }

  async getEthersProvider(): Promise<ethers.BrowserProvider> {
    if (!this.ethersProvider) throw new Error('Wallet not connected');
    return this.ethersProvider;
  }

  async getSigner(): Promise<ethers.JsonRpcSigner> {
    if (!this.ethersProvider) throw new Error('Wallet not connected');
    return await this.ethersProvider.getSigner();
  }

  /**
   * Switch Network
   */
  async switchNetwork(chainIdOrName: string | number): Promise<void> {
    try {
      let targetChainId: number;
      if (typeof chainIdOrName === 'string') {
        const config = getNetworkConfigById(chainIdOrName);
        if (config) {
          targetChainId = config.chainId;
        } else {
          targetChainId = getNetworkConfigById('eth')!.chainId;
        }
      } else {
        targetChainId = chainIdOrName;
      }

      const networks = [mainnet, customSepolia, bsc, polygon, bscTestnet, polygonAmoy];
      const network = networks.find((n) => n.id === targetChainId);
      if (network) {
        await appkit.switchNetwork(network);
      }
    } catch (e) {
      console.error('Failed to switch network via AppKit', e);
      throw e;
    }
  }

  /**
   * Enforce correct network based on liquidity type
   */
  async enforceNetwork(liquidityType: 'internal' | 'external' | string): Promise<void> {
    if (!this.isConnected()) return;

    // @ts-ignore (Environment definition compatibility)
    const targetConfig = liquidityType === 'internal'
      ? (environment as any).networks?.internal
      : (environment as any).networks?.external;

    if (targetConfig && this.chainId() !== targetConfig.chainId) {
      await this.switchNetwork(targetConfig.chainId);
      await new Promise(r => setTimeout(r, 1000));
    }
  }

  /**
   * Sync balance and network details
   */
  private async syncAccountState() {
    if (!this.ethersProvider) return;
    try {
      const addr = this.address();
      if (!addr) return;

      const provider = this.getReadProvider();
      const bal = await provider.getBalance(addr);
      this.balance.set(parseFloat(ethers.formatEther(bal)));
    } catch (e) {
      console.error('Error syncing account state:', e);
    }
  }

  /**
   * Disconnect wallet via AppKit
   */
  async disconnect(): Promise<void> {
    try {
      await appkit.disconnect();
    } catch (e) {
      console.error('Disconnect failed', e);
    }
  }

  /**
   * Request signature from the connected wallet
   */
  async signMessage(message: string): Promise<string> {
    if (!this.ethersProvider) {
      throw new Error('Wallet is not connected');
    }
    const address = this.address();
    if (!address) throw new Error('No active address to sign with');

    // Force request accounts to ensure the provider knows about the session
    await this.ethersProvider.send("eth_requestAccounts", []);
    const signer = await this.ethersProvider.getSigner(address);
    return await signer.signMessage(message);
  }

  /**
   * Get Dynamic Contracts Config for current network
   */
  getContractsConfig() {
    // If not connected, default to the app's selected default network
    const currentChainId = this.isConnected() ? this.chainId() : environment.networks.internal.chainId;
    // @ts-ignore
    const config = environment.contracts[currentChainId];
    if (!config) {
      console.warn(`No contract config found for chainId: ${currentChainId}. Falling back to default.`);
      // @ts-ignore
      return environment.contracts[environment.networks.internal.chainId];
    }
    return config;
  }

  /**
   * Get Ethers Contract Instance
   */
  async getContract(address: string, abi: any[]): Promise<ethers.Contract> {
    if (!this.ethersProvider) throw new Error('Wallet not connected');
    const signer = await this.ethersProvider.getSigner();
    return new ethers.Contract(address, abi, signer);
  }

  /**
   * Get an independent JSON RPC Provider for the currently selected UI network
   */
  getReadProvider(): ethers.JsonRpcProvider {
    const currentChainId = this.chainId() || environment.networks.internal.chainId;
    const networks = NETWORKS;
    let rpcUrl = networks['eth'].rpcUrls[0];
    for (const key in networks) {
      if (networks[key].chainId === currentChainId) {
        rpcUrl = networks[key].rpcUrls[0];
        break;
      }
    }
    return new ethers.JsonRpcProvider(rpcUrl);
  }

  /**
   * Get Ethers Contract Instance for Read-Only operations
   */
  async getReadContract(address: string, abi: any[]): Promise<ethers.Contract> {
    const fallbackProvider = this.getReadProvider();
    return new ethers.Contract(address, abi, fallbackProvider);
  }

  async getSwapRouter(): Promise<ethers.Contract> {
    const contracts = this.getContractsConfig();
    return this.getContract(contracts.SWAP_ROUTER, SWAP_ROUTER_ABI);
  }

  async getPositionManager(): Promise<ethers.Contract> {
    const contracts = this.getContractsConfig();
    return this.getContract(contracts.POSITION_MANAGER, POSITION_MANAGER_ABI);
  }

  /**
   * Check and Request ERC20 Approval
   */
  async checkAndApprove(
    tokenAddress: string,
    spenderAddress: string,
    amountToSpend: bigint,
  ): Promise<void> {
    if (!this.ethersProvider) throw new Error('Wallet not connected');

    // Skip approval for Native Coin
    if (tokenAddress.toLowerCase() === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee') {
      return;
    }

    const signer = await this.ethersProvider.getSigner();
    const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, signer);

    const owner = await signer.getAddress();
    let allowance: bigint;

    try {
      allowance = await tokenContract['allowance'](owner, spenderAddress);
    } catch (err: any) {
      console.error(`Failed to read allowance for token ${tokenAddress}:`, err);
      if (err.code === 'BAD_DATA') {
        throw new Error(
          `The token at address ${tokenAddress} is not deployed on the network you are currently connected to. Please ensure your wallet is on the correct network (e.g. Testnet vs Mainnet) and that the token exists.`,
        );
      }
      throw new Error(
        `Failed to interact with token ${tokenAddress}. Is it a valid ERC20 contract?`,
      );
    }

    if (allowance < amountToSpend) {
      // Need approval
      console.log(`Approving ${tokenAddress} for ${spenderAddress}`);
      const tx = await tokenContract['approve'](spenderAddress, ethers.MaxUint256);
      if (!tx) {
        throw new Error('Approval transaction returned undefined.');
      }
      await this.waitForTransaction(tx.hash);
      console.log('Approval successful');
    }
  }

  /**
   * Send Raw Transaction
   */
  async sendRawTransaction(
    to: string,
    data: string,
    value: string = '0',
  ): Promise<ethers.TransactionResponse> {
    if (!this.ethersProvider) throw new Error('Wallet not connected');
    const signer = await this.ethersProvider.getSigner();

    return await signer.sendTransaction({
      to: to,
      data: data,
      value: value,
    });
  }

  /**
   * Sign EIP-712 Typed Data
   */
  async signTypedData(domain: any, types: any, value: any): Promise<string> {
    if (!this.ethersProvider) throw new Error('Wallet not connected');
    const signer = await this.ethersProvider.getSigner();

    // Ethers.js automatically handles EIP712Domain, so we must remove it if present in the payload
    const cleanedTypes = { ...types };
    if (cleanedTypes['EIP712Domain']) {
      delete cleanedTypes['EIP712Domain'];
    }

    return await signer.signTypedData(domain, cleanedTypes, value);
  }


  async waitForTransaction(txHash: string, confirmations: number = 1): Promise<ethers.TransactionReceipt | null> {
    let provider: ethers.Provider | null = null;
    try {
      provider = await this.getEthersProvider();
    } catch (e) {
      const networks = NETWORKS;
      let rpcUrl = networks['eth'].rpcUrls[0];
      for (const key in networks) {
        if (networks[key].chainId === this.chainId()) {
          rpcUrl = networks[key].rpcUrls[0];
          break;
        }
      }
      provider = new ethers.JsonRpcProvider(rpcUrl);
    }

    let receipt: ethers.TransactionReceipt | null = null;
    let attempts = 0;
    while (!receipt && attempts < 120) { // Max 10 minutes wait
      try {
        const _receipt = await provider.getTransactionReceipt(txHash);
        if (_receipt && (await _receipt.confirmations()) >= confirmations) {
          return _receipt;
        }
      } catch (e) {
        console.log("error_11", e)
      }
      await new Promise(resolve => setTimeout(resolve, 2000));
      attempts++;
    }
    return receipt;
  }

  async wrapNative(wrappedAddress: string, amount: bigint): Promise<ethers.TransactionResponse> {
    const signer = await this.getSigner();
    const wethContract = new ethers.Contract(wrappedAddress, ['function deposit() public payable'], signer);
    return await wethContract['deposit']({ value: amount });
  }

  async unwrapNative(wrappedAddress: string, amount: bigint): Promise<ethers.TransactionResponse> {
    const signer = await this.getSigner();
    const wethContract = new ethers.Contract(wrappedAddress, ['function withdraw(uint256 wad) public'], signer);
    return await wethContract['withdraw'](amount);
  }

  /**
   * Remove Liquidity from V3 Position
   * Bundles decreaseLiquidity and collect using multicall
   */
  async removeLiquidity(
    tokenId: number,
    liquidityToRemove: bigint,
    amount0Min: bigint = 0n,
    amount1Min: bigint = 0n,
    managerAddress?: string,
  ): Promise<ethers.TransactionResponse> {
    if (!this.ethersProvider) throw new Error('Wallet not connected');

    const signer = await this.ethersProvider.getSigner();
    let manager = await this.getPositionManager();
    if (managerAddress) {
      manager = new ethers.Contract(managerAddress, POSITION_MANAGER_ABI, signer);
    } else {
      manager = manager.connect(signer) as ethers.Contract;
    }
    const owner = await signer.getAddress();
    const deadline = Math.floor(Date.now() / 1000) + 60 * 20;

    // 1. decreaseLiquidity calldata
    const decreaseParams = {
      tokenId: tokenId,
      liquidity: liquidityToRemove,
      amount0Min: amount0Min,
      amount1Min: amount1Min,
      deadline: deadline,
    };
    const decreaseCalldata = manager.interface.encodeFunctionData('decreaseLiquidity', [
      decreaseParams,
    ]);

    // 2. collect calldata (MaxUint128 to pull everything available)
    const MAX_UINT128 = 2n ** 128n - 1n;
    const collectParams = {
      tokenId: tokenId,
      recipient: owner,
      amount0Max: MAX_UINT128,
      amount1Max: MAX_UINT128,
    };
    const collectCalldata = manager.interface.encodeFunctionData('collect', [collectParams]);

    // 3. multicall
    console.log(`Executing multicall: decreaseLiquidity + collect for NFT #${tokenId}`);
    return await manager['multicall']([decreaseCalldata, collectCalldata]);
  }

  /**
   * Claim Fees from V3 Position
   */
  async claimFees(tokenId: number, managerAddress?: string): Promise<ethers.TransactionResponse> {
    if (!this.ethersProvider) throw new Error('Wallet not connected');

    let manager = await this.getPositionManager();
    const signer = await this.ethersProvider.getSigner();

    if (managerAddress) {
      manager = new ethers.Contract(managerAddress, POSITION_MANAGER_ABI, signer);
    } else {
      manager = manager.connect(signer) as ethers.Contract;
    }
    const owner = await signer.getAddress();

    const MAX_UINT128 = 2n ** 128n - 1n;
    const collectParams = {
      tokenId: tokenId,
      recipient: owner,
      amount0Max: MAX_UINT128,
      amount1Max: MAX_UINT128,
    };

    console.log(`Executing collect for NFT #${tokenId}`);
    return await manager['collect'](collectParams);
  }

  /**
   * Get Live Pending Fees from V3 Position (Read-Only)
   */
  async getPendingFees(tokenId: number, managerAddress?: string): Promise<{ amount0: bigint; amount1: bigint }> {
    if (!this.ethersProvider) throw new Error('Wallet not connected');

    const provider = this.getReadProvider();
    const signer = await this.ethersProvider.getSigner();
    const owner = await signer.getAddress();

    let manager: ethers.Contract;
    if (managerAddress) {
      manager = new ethers.Contract(managerAddress, POSITION_MANAGER_ABI, provider);
    } else {
      const defaultManager = await this.getPositionManager();
      manager = new ethers.Contract(await defaultManager.getAddress(), POSITION_MANAGER_ABI, provider);
    }

    try {
      // 1. Fetch current position to get its liquidity
      const posData = await manager['positions'](tokenId);
      const currentLiquidity = posData.liquidity;

      console.log(`[getPendingFees ${tokenId}] posData from blockchain:`, posData);
      console.log(`[getPendingFees ${tokenId}] currentLiquidity from blockchain:`, currentLiquidity);

      // If liquidity is 0, we can't decrease it. Just return what's owed.
      if (currentLiquidity === 0n) {
        console.log(`[getPendingFees ${tokenId}] Returning tokensOwed because currentLiquidity is 0`);
        return {
          amount0: posData.tokensOwed0 || 0n,
          amount1: posData.tokensOwed1 || 0n,
        };
      }

      // 2. decreaseLiquidity with FULL liquidity to trigger fee accrual update without reverting
      const decreaseParams = {
        tokenId: tokenId,
        liquidity: currentLiquidity,
        amount0Min: 0n,
        amount1Min: 0n,
        deadline: Math.floor(Date.now() / 1000) + 60 * 20,
      };
      const decreaseCalldata = manager.interface.encodeFunctionData('decreaseLiquidity', [
        decreaseParams,
      ]);

      // 3. collect everything
      const MAX_UINT128 = 2n ** 128n - 1n;
      const collectParams = {
        tokenId: tokenId,
        recipient: owner,
        amount0Max: MAX_UINT128,
        amount1Max: MAX_UINT128,
      };
      const collectCalldata = manager.interface.encodeFunctionData('collect', [collectParams]);

      // Simulate multicall execution from the owner's address
      const results = await manager['multicall'].staticCall([decreaseCalldata, collectCalldata], { from: owner });

      const decreaseResult = manager.interface.decodeFunctionResult('decreaseLiquidity', results[0]);
      const collectResult = manager.interface.decodeFunctionResult('collect', results[1]);

      console.log(`[getPendingFees ${tokenId}] decreaseResult:`, decreaseResult);
      console.log(`[getPendingFees ${tokenId}] collectResult:`, collectResult);

      const fees = {
        amount0: BigInt(collectResult[0]) - BigInt(decreaseResult[0]), // total0 - principal0
        amount1: BigInt(collectResult[1]) - BigInt(decreaseResult[1]), // total1 - principal1
      };

      console.log(`[getPendingFees ${tokenId}] calculated fees:`, fees);
      return fees;
    } catch (e) {
      console.error(`Could not fetch live pending fees for NFT #${tokenId} via multicall.`, e);
      return { amount0: 0n, amount1: 0n };
    }
  }

  // --- Farming Methods ---

  async getFarmingContract(): Promise<ethers.Contract> {
    const contracts = this.getContractsConfig();
    return this.getContract(contracts.FARMING, FARMING_ABI);
  }

  async getFarmPid(targetFarmAddress: string): Promise<number | null> {
    try {
      const farming = await this.getFarmingContract();
      const length: bigint = await farming['poolLength']();
      for (let i = 0; i < Number(length); i++) {
        const poolInfo = await farming['poolInfo'](i);
        const targetPool = poolInfo.targetPool || poolInfo[0];
        if (targetPool.toLowerCase() === targetFarmAddress.toLowerCase()) {
          return i;
        }
      }
      return null;
    } catch (e) {
      console.error('Error fetching farm PID', e);
      return null;
    }
  }

  async unstake(pid: number, tokenId: number): Promise<ethers.TransactionResponse> {
    if (!this.ethersProvider) throw new Error('Wallet not connected');
    const farming = await this.getFarmingContract();
    console.log(`Unstaking NFT #${tokenId} from farm pid ${pid}`);
    return await farming['unstake'](pid, tokenId);
  }

  async claimReward(pid: number): Promise<ethers.TransactionResponse> {
    if (!this.ethersProvider) throw new Error('Wallet not connected');
    const farming = await this.getFarmingContract();
    console.log(`Claiming rewards for farm pid ${pid}`);
    return await farming['claim'](pid);
  }

  async getPendingReward(pid: number): Promise<bigint> {
    if (!this.ethersProvider) throw new Error('Wallet not connected');
    const farming = await this.getFarmingContract();
    const signer = await this.ethersProvider.getSigner();
    const owner = await signer.getAddress();
    return await farming['pendingReward'](pid, owner);
  }

  async emergencyWithdrawFarm(pid: number, tokenId: number): Promise<ethers.TransactionResponse> {
    if (!this.ethersProvider) throw new Error('Wallet not connected');
    const farming = await this.getFarmingContract();
    console.log(`Emergency withdrawing NFT #${tokenId} from farm pid ${pid}`);
    return await farming['emergencyWithdraw'](pid, tokenId);
  }

  async emergencyWithdrawStaking(stakingAddress: string, depositIndex: number, poolId: number): Promise<ethers.TransactionResponse> {
    if (!this.ethersProvider) throw new Error('Wallet not connected');
    const signer = await this.getSigner();
    const stakingContract = new ethers.Contract(
      stakingAddress,
      ['function emergencyWithdraw(uint256 poolId, uint256 depositIndex) external'],
      signer,
    );
    console.log(`Emergency withdrawing deposit #${depositIndex} from staking`);
    return await stakingContract['emergencyWithdraw'](poolId, depositIndex);
  }

  async depositStaking(
    tokenAddress: string,
    stakingAddress: string,
    poolId: number,
    amountWei: bigint,
    tierIndex: number,
    statusCallback?: (status: string) => void,
  ) {
    try {
      const isNative = tokenAddress.toLowerCase() === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee';

      if (statusCallback) statusCallback('CHECKING WALLET...');
      const signer = await this.getSigner();

      let tokenContract: ethers.Contract | null = null;
      if (!isNative) {
        tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, signer);
      }

      if (statusCallback) statusCallback('CHECKING BALANCE...');
      let balance: bigint;
      try {
        if (isNative) {
          balance = await this.ethersProvider!.getBalance(this.address());
        } else {
          balance = await tokenContract!['balanceOf'](this.address());
        }
      } catch (err: any) {
        if (err.code === 'BAD_DATA' || String(err).includes('BAD_DATA')) {
          throw new Error(
            `Token contract not found at ${tokenAddress} on this network. Are you on the right network?`,
          );
        }
        throw err;
      }

      try {
        const decimals = isNative ? 18 : await tokenContract!['decimals']();
        console.log('--- STAKING DEBUG ---');
        console.log('Token Address:', tokenAddress);
        console.log('Staking Address:', stakingAddress);
        console.log('Wallet Address:', this.address());
        console.log('Token Decimals:', decimals.toString());
        console.log('Amount (Wei):', amountWei.toString());
        console.log('Balance (Wei):', balance.toString());
        console.log('Amount (Formatted):', ethers.formatUnits(amountWei, decimals));
        console.log('Balance (Formatted):', ethers.formatUnits(balance, decimals));
        console.log('---------------------');
      } catch (e) {
        console.log('Staking Debug: Could not fetch decimals', e);
        console.log('Amount (Wei):', amountWei.toString(), 'Balance (Wei):', balance.toString());
      }

      if (balance < amountWei) {
        throw new Error('Insufficient token balance to stake this amount.');
      }

      if (!isNative) {
        if (statusCallback) statusCallback('APPROVING...');
        const allowance = await tokenContract!['allowance'](this.address(), stakingAddress);

        if (allowance < amountWei) {
          const approveTx = await tokenContract!['approve'](stakingAddress, ethers.MaxUint256);
          if (statusCallback) statusCallback('WAITING FOR APPROVAL TO MINE...');
          await this.waitForTransaction(approveTx.hash);
        }
      }

      const stakingContract = new ethers.Contract(
        stakingAddress,
        ['function deposit(uint256 poolId, uint256 amount, uint256 tierIndex) payable'],
        signer,
      );

      if (statusCallback) statusCallback('DEPOSITING...');
      const txOptions: any = {};
      if (isNative) {
        txOptions.value = amountWei;
      }
      const tx = await stakingContract['deposit'](poolId, amountWei, tierIndex, txOptions);
      if (statusCallback) statusCallback('WAITING FOR DEPOSIT TO MINE...');
      return await this.waitForTransaction(tx.hash);
    } catch (error) {
      console.error('depositStaking error:', error);
      throw error;
    }
  }

  async getExternalPoolSlot0(poolAddress: string): Promise<any> {
    try {
      const chainId = this.chainId();
      let rpcUrl = (environment as any).rpcUrls?.[chainId];
      if (!rpcUrl && Object.values((environment as any).rpcUrls).length > 0) {
        rpcUrl = Object.values((environment as any).rpcUrls)[0];
      }
      console.log('rpc_url', rpcUrl);
      const provider = new ethers.JsonRpcProvider(rpcUrl);
      const abi = ['function slot0() external view returns (uint160 sqrtPriceX96, int24 tick, uint16 observationIndex, uint16 observationCardinality, uint16 observationCardinalityNext, uint8 feeProtocol, bool unlocked)'];
      const contract = new ethers.Contract(poolAddress, abi, provider);
      // console.log('slot0', await contract['slot0']()); // Removed to avoid double call
      return await contract['slot0']();
    } catch (error) {
      console.warn("getExternalPoolSlot0 warning: could not fetch slot0 for", poolAddress);
      return null;
    }
  }
}